import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserCheck } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type PlanAssignments,
  PlanAssignments as Schema,
  type PlanDraftView,
} from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { errorMessage } from '../../../lib/error-message';
import { AiSkillPanel } from '../../ai/components/ai-skill-panel';
import { planApi, planKeys } from '../api';

/**
 * "Ai làm việc nào" cho một bản nháp — APM-UX-ai4.
 *
 * ## Vì sao nó là một mục riêng trong màn duyệt nháp
 *
 * Kế hoạch sinh ra có Gantt nhưng chưa có người. Chỗ trả lời câu hỏi "giao cho
 * ai" phải nằm đúng nơi người ta đang nhìn kế hoạch, chứ không phải một màn
 * hình khác — và nó phải chạy lại được **mà không sinh lại kế hoạch**, vì giờ
 * rảnh của đội đổi hằng tuần còn cách chia việc thì không.
 *
 * ## Vì sao dùng `AiSkillPanel`
 *
 * Bốn thứ đi kèm mọi tính năng AI ở đây đều giống nhau và đều dễ quên đúng một
 * chỗ: nút chạy (hoặc đường làm tay khi chưa bật AI), theo dõi lượt chạy, đọc
 * lại gợi ý đã lưu sau F5, và ba cảnh báo của harness. Chép tay lần thứ mười
 * thì chúng trôi khỏi nhau ở chỗ khó thấy nhất — chỗ cảnh báo.
 */
export function AssignSection({
  draft,
  members,
  startDate,
}: {
  readonly draft: PlanDraftView;
  readonly members: readonly { readonly userId: string; readonly displayName: string | null }[];
  /** Ngày khởi động đang chọn ở màn duyệt — quyết định cửa sổ ngày để tra ai rảnh. */
  readonly startDate: string;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const nameOf = (userId: string): string =>
    members.find((member) => member.userId === userId)?.displayName ?? userId.slice(0, 8);
  const titleOf = (itemId: string): string =>
    draft.items.find((item) => item.id === itemId)?.title ?? itemId.slice(0, 8);

  const apply = useMutation({
    mutationFn: (content: PlanAssignments) =>
      planApi.assign(
        draft.id,
        content.assignments.map((item) => ({ itemId: item.itemId, userId: item.userId })),
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData<PlanDraftView[]>(planKeys.list(draft.projectId), (current) =>
        (current ?? []).map((item) => (item.id === updated.id ? updated : item)),
      );
      toast.success(t('plan.assign.applied'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  return (
    <AiSkillPanel
      skill="assign_plan_items"
      projectId={draft.projectId}
      title={t('plan.assign.title')}
      /*
        Mở sẵn khi **chưa hạng mục nào có người** — APM-UX-ai4.

        `AiSkillPanel` mặc định đóng, và đóng là đúng cho chín mục AI rải khắp
        các tab: chúng trả lời những câu hỏi thỉnh thoảng mới hỏi. Mục này thì
        khác — một kế hoạch vừa sinh ra **luôn** chưa có người, nên câu hỏi "giao
        cho ai" là câu tiếp theo của mọi lần mở nháp mới. Đóng nó lại thành một
        dòng viền mỏng giữa sáu mươi hạng mục là giấu đi đúng thứ đang cần.

        Giao xong thì nó tự đóng: lúc ấy nó lại thành một tính năng thỉnh thoảng
        dùng (giao lại khi đội đổi lịch), đúng như chín mục kia.
      */
      defaultOpen={draft.items.every((item) => item.suggestedAssigneeId === null)}
      help={t('plan.assign.help')}
      icon={UserCheck}
      label={t('plan.assign.run')}
      /*
        ⚠️ **Không bao giờ để rỗng.** `RunSkillInput.prompt` là `z.string().min(1)`,
        nên ô trống làm Zod trả 400 cho cả request và người dùng chỉ thấy "Dữ
        liệu chưa hợp lệ" — một thông điệp nói sai chỗ, cạnh một ô ghi rõ "không
        bắt buộc".

        Ba mục free-text khác (`what_if`, `scope_negotiation`,
        `decision_archaeology`) né được bằng `canRun`, vì với chúng ô text **là**
        câu hỏi. Ở đây nó thật sự tuỳ chọn, nên khoá nút là fix sai: phải gửi một
        câu mặc định, và câu ấy còn nói cho model biết rằng không có ràng buộc
        nào — khác hẳn với việc im lặng.
      */
      prompt={note.trim() || t('plan.assign.defaultPrompt')}
      params={{ draftId: draft.id, startDate }}
      schema={Schema}
      routeParams={{ projectId: draft.projectId }}
      controls={
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground" htmlFor={`assign-note-${draft.id}`}>
            {t('plan.assign.noteLabel')}
          </label>
          <input
            id={`assign-note-${draft.id}`}
            className="h-8 w-full rounded-control border border-border-strong bg-background px-2 text-sm"
            placeholder={t('plan.assign.notePlaceholder')}
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      }
      render={(content) => (
        <div className="flex flex-col gap-2">
          {content.assignments.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('plan.assign.noneAssigned')}</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {content.assignments.map((item) => (
                <li key={item.itemId} className="text-sm">
                  <p>
                    <span className="font-medium">{titleOf(item.itemId)}</span>
                    {' → '}
                    {nameOf(item.userId)}
                  </p>
                  {/* Lý do đứng ngay dưới: người duyệt bác được một đề xuất chỉ
                      khi đọc được căn cứ của nó (luật #6). */}
                  <p className="text-xs text-muted-foreground">{item.reason}</p>
                </li>
              ))}
            </ul>
          )}

          {content.unassigned.length > 0 ? (
            <div className="border-t border-border pt-2">
              <p className="text-xs font-medium">{t('plan.assign.unassigned')}</p>
              <ul className="mt-1 flex flex-col gap-1">
                {content.unassigned.map((item) => (
                  <li key={item.itemId} className="text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{titleOf(item.itemId)}</span>
                    {' — '}
                    {item.reason}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
      actions={(content) =>
        content.assignments.length === 0 ? null : (
          <Button size="sm" disabled={apply.isPending} onClick={() => apply.mutate(content)}>
            {t('plan.assign.apply', { count: content.assignments.length })}
          </Button>
        )
      }
    />
  );
}
